import { errorHandler } from "@/APIs/errorHandler";

/**
 * Format a number with a leading 0 when under 10
 * @param number
 * @returns string
 */
export const formatNumberWithLeading0 = (number: any): string => {
  return `${number}`.padStart(2, "0");
};

export const formatCurrency = (
  amount: number,
  currency: string = "GBP"
): string => {
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency
  });
};

export const parseDepositAmount = (
  amount: number | { amount: number }
): number => {
  return typeof amount === "object"
    ? parseFloat(amount.amount?.toString() || "0")
    : parseFloat(amount?.toString() || "0");
};

/**
 * Get the currency symbol for a given currency code using Intl.NumberFormat
 * @param currency - The currency code (e.g., 'GBP', 'USD', 'EUR')
 * @returns Currency symbol
 */
const MAX_SERVICE_CHARGE_PERCENTAGE = 100;

/**
 * Validate and gate service charge input. Only allows empty strings
 * or valid positive decimal numbers (e.g. "12", "12.5", ".5", "12.").
 * Uses character-level checks instead of regex to avoid ReDoS risks.
 * @param value - The raw input value
 * @returns Whether the value is a valid service charge input
 */
export const isValidServiceChargeInput = (value: string): boolean => {
  if (value === "") return true;

  let dotSeen = false;
  for (const ch of value) {
    if (ch === ".") {
      if (dotSeen) return false;
      dotSeen = true;
    } else if (ch < "0" || ch > "9") {
      return false;
    }
  }

  return true;
};

/**
 * Parse a service charge string value into a clamped number
 * suitable for saving. Clamps between 0 and 100.
 * @param value - The string value from the input
 * @returns A number between 0 and 100
 */
export const parseServiceChargeValue = (value: string): number => {
  const parsed = parseFloat(value);
  if (isNaN(parsed) || parsed < 0) return 0;
  return Math.min(parsed, MAX_SERVICE_CHARGE_PERCENTAGE);
};

export const getCurrencySymbol = (currency: string): string => {
  if (!currency || currency.trim() === "") {
    return currency;
  }
  try {
    const formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    const parts = formatter.formatToParts(0);
    const currencyPart = parts.find((part) => part.type === "currency");
    return currencyPart?.value ?? currency;
  } catch (error) {
    errorHandler(error, "");
    return currency;
  }
};
